/**
 * Shared colour-coded pill rendering the TrustX® qualifier on a question.
 *
 * Brand Excellence   → blue
 * Sales Excellence   → amber
 * Service Excellence → teal
 * Anything else      → gray (or null if no qualifier configured)
 *
 * Resolution: the question carries a `qualifier_id` FK; the parent survey
 * carries a `qualifiers: [{id, label}]` array. The pill renders the resolved
 * label with a colour mapped from the label text (case-insensitive substring).
 */
import React from 'react';

const PALETTE = [
  { match: /brand/i,   bg: '#E6F1FB', fg: '#0C447C', label: 'Brand Excellence' },
  { match: /sales/i,   bg: '#FAEEDA', fg: '#633806', label: 'Sales Excellence' },
  { match: /service/i, bg: '#E1F5EE', fg: '#085041', label: 'Service Excellence' },
];

function resolveQualifierLabel(qualifierId, qualifiers) {
  if (!qualifierId || !Array.isArray(qualifiers)) return null;
  const hit = qualifiers.find(q => (q.id || q.qualifier_id) === qualifierId);
  return hit ? (hit.label || hit.name || null) : null;
}

function paletteFor(label) {
  if (!label) return null;
  for (const p of PALETTE) {
    if (p.match.test(label)) return { bg: p.bg, fg: p.fg };
  }
  // Unknown qualifier — neutral gray.
  return { bg: '#F1F5F9', fg: '#334155' };
}

/**
 * Compact pill. Accepts either a resolved `label` directly, OR a
 * `qualifierId` + `qualifiers` array to resolve from.
 */
export default function QualifierPill({ label, qualifierId, qualifiers, size = 'sm', className = '' }) {
  const resolvedLabel = label || resolveQualifierLabel(qualifierId, qualifiers);
  if (!resolvedLabel) return null;
  const palette = paletteFor(resolvedLabel);
  const pad = size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs';
  return (
    <span
      data-testid={`qualifier-pill-${resolvedLabel.toLowerCase().replace(/\s+/g, '-')}`}
      title={`TrustX® dimension: ${resolvedLabel}`}
      className={`inline-flex items-center rounded-full font-semibold uppercase tracking-wide ${pad} ${className}`}
      style={{ backgroundColor: palette.bg, color: palette.fg }}
    >
      {resolvedLabel}
    </span>
  );
}
